import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Wrench, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ServiceTypesCardProps {
  serviceTypes?: string[] | null;
  category?: string;
  className?: string;
}

// Format service type for display
const formatServiceType = (type: string): string => {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

// Group service types by category for better display
const getCategoryColor = (type: string): string => {
  const lower = type.toLowerCase();
  
  if (lower.includes('maintenance') || lower.includes('repair')) {
    return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
  }
  if (lower.includes('inspection') || lower.includes('check')) {
    return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  }
  if (lower.includes('overhaul')) {
    return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
  }
  if (lower.includes('modification') || lower.includes('upgrade')) {
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
  }
  if (lower.includes('training') || lower.includes('course')) {
    return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
  }
  
  return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
};

export const ServiceTypesCard: React.FC<ServiceTypesCardProps> = ({
  serviceTypes,
  category,
  className
}) => {
  if (!serviceTypes || serviceTypes.length === 0) return null;

  return (
    <Card className={cn("border-border", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Wrench className="w-4 h-4 text-primary" />
          Services Offered
          <Badge variant="secondary" className="text-[10px] ml-auto">
            {serviceTypes.length} service{serviceTypes.length !== 1 ? 's' : ''}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-1.5">
          {serviceTypes.map((type, idx) => (
            <Badge 
              key={idx} 
              variant="outline" 
              className={cn("text-[10px] gap-1", getCategoryColor(type))}
            >
              <Check className="w-2.5 h-2.5" />
              {formatServiceType(type)}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceTypesCard;
